import React, { useState, useEffect, useContext } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { 
  IconWaveSawTool, 
  IconUsers, 
  IconCheck, 
  IconTrendingUp, 
  IconRobot, 
  IconMessageCircle,
  IconCreditCard,
  IconSpeakerphone,
  IconPalette,
  IconHandStop,
  IconUser,
  IconChartBar,
  IconTrophy,
  IconEye,
  IconStar,
  IconBulb,
  IconHeart
} from '@tabler/icons-react';
import UserContext from '../contexts/UserContext';
import DashboardService from '../jsx/components/Dashboard/DashboardServices';
import './Welcome/css/welcomeScreen.css';

const AgencyWelcome = () => {
  const { user, getUserPlan } = useContext(UserContext);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadMetrics = async () => {
    try {
      setError(null);
      const data = await DashboardService.getAgencyMetrics();
      setMetrics(data);
    } catch (err) {
      console.error('❌ Erro ao carregar métricas:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadMetrics();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await DashboardService.forceAgencyRefresh();
    await loadMetrics();
  };

  // Função para obter dados de métricas formatados
  const getMetricsData = () => {
    if (!metrics) return null;

    return {
      activeSubaccounts: metrics.organization_overview?.total_subaccounts?.active || 0,
      totalAgents: metrics.agent_metrics?.total_agents_created || 0,
      implementedAgents: metrics.agent_metrics?.agents_implemented || 0,
      conversionRate: metrics.agent_metrics?.conversion_rate || 0,
      totalMessages: metrics.engagement_metrics?.total_messages?.total || 0,
      activeUsers: metrics.organization_overview?.total_users || 0,
      churnRate: metrics.deployment_metrics?.churn_rate || 0
    };
  };

  // Função para obter os top performers
  const getTopPerformers = () => {
    if (!metrics?.engagement_metrics?.most_active_subaccounts) return [];
    
    return metrics.engagement_metrics.most_active_subaccounts
      .slice(0, 3)
      .map((account, index) => ({
        position: index + 1,
        name: account.account_name || `Cliente ${index + 1}`,
        messages: account.message_count || 0,
        emoji: index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'
      }));
  };

  if (loading) {
    return (
      <div className="welcome-container">
        <div className="text-center mt-5">
          <Spinner animation="border" variant="primary" size="lg" />
          <p className="mt-3 text-muted">Carregando sua central de agência...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="welcome-container">
        <Container>
          <Alert variant="danger" className="mt-4">
            <Alert.Heading>Ops! Algo deu errado</Alert.Heading>
            <p>{error}</p>
            <Button variant="outline-danger" onClick={handleRefresh} disabled={refreshing}>
              {refreshing ? 'Tentando novamente...' : 'Tentar Novamente'}
            </Button>
          </Alert>
        </Container>
      </div>
    );
  }

  const orgName = metrics?.organization_overview?.organization_name || 'BrandName';
  const metricsData = getMetricsData();
  const topPerformers = getTopPerformers();

  return (
    <div className="welcome-container">
      {/* Header de Boas-vindas */}
      <section className="welcome-header">
        <Container>
          <div className="welcome-hero">
            <div className="welcome-hero-content">
              <div className="welcome-hero-text">
                <h1 className="welcome-title text-capitalize">
                  👋 Olá, {user?.first_name || "Usuário"}!
                </h1>

                <p className="welcome-subtitle">
                  Seja bem-vindo! <br></br>Aqui você gerencia clientes, agentes
                  e resultados de forma simples.
                </p>
                {refreshing && (
                  <p className="welcome-subtitle">
                    <small>🔄 Atualizando dados...</small>
                  </p>
                )}
              </div>
              <div className="welcome-hero-decoration">
                <div className="welcome-decoration-item">
                  <IconStar size={24} />
                </div>
                <div className="welcome-decoration-item">
                  <IconBulb size={28} />
                </div>
                <div className="welcome-decoration-item">
                  <IconHeart size={20} />
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* O que você deseja fazer hoje */}
      <section>
        <Container>
          <div className="welcome-category-section">
            <div className="welcome-category-header">
              <h2 className="welcome-category-title pt-5">
                O que você deseja fazer hoje?
              </h2>
            </div>
            <Row>
              <Col xs={12} sm={6} lg={4} className="mb-4">
                <Card className="welcome-module-card h-100">
                  <Card.Body className="d-flex flex-column">
                    <div className="welcome-module-header">
                      <div
                        className="welcome-module-icon"
                        style={{
                          background:
                            "var(--gradient-primary)",
                        }}
                      >
                        <IconRobot size={24} />
                      </div>
                    </div>
                    <h5 className="welcome-module-title">Criar Agente</h5>
                    <p className="welcome-module-description flex-grow-1">
                      Configure um novo agente de IA para seus clientes.
                    </p>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => (window.location.href = "/agents-flow")}
                    >
                      Ir para criação
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={12} sm={6} lg={4} className="mb-4">
                <Card className="welcome-module-card h-100">
                  <Card.Body className="d-flex flex-column">
                    <div className="welcome-module-header">
                      <div
                        className="welcome-module-icon"
                        style={{
                          background:
                            "linear-gradient(135deg, #de3096, #e83e8c)",
                        }}
                      >
                        <IconUser size={24} />
                      </div>
                    </div>
                    <h5 className="welcome-module-title">Criar Novo Cliente</h5>
                    <p className="welcome-module-description flex-grow-1">
                      Adicione um novo cliente à sua agência e comece a
                      gerenciar sua conta.
                    </p>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() =>
                        (window.location.href = "/organization-clients")
                      }
                    >
                      Ir para clientes
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={12} sm={6} lg={4} className="mb-4">
                <Card className="welcome-module-card h-100">
                  <Card.Body className="d-flex flex-column">
                    <div className="welcome-module-header">
                      <div
                        className="welcome-module-icon"
                        style={{
                          background:
                            "var(--gradient-primary)",
                        }}
                      >
                        <IconChartBar size={24} />
                      </div>
                    </div>
                    <h5 className="welcome-module-title">Métricas</h5>
                    <p className="welcome-module-description flex-grow-1">
                      Acompanhe resultados, crescimento e engajamento da sua
                      agência.
                    </p>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => (window.location.href = "/metrics")}
                    >
                      Ver métricas
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        </Container>
      </section>

      {/* Indicadores-chave da Agência */}
      <section>
        <Container>
          <div className="welcome-category-section">
            <div className="welcome-category-header">
              <h2 className="welcome-category-title">
                <IconChartBar className="me-2" />
                Indicadores-chave da Agência
              </h2>
            </div>
            <Row>
              <Col md={4} className="mb-4">
                <Card className="welcome-module-card h-100">
                  <Card.Body className="text-center">
                    <div className="welcome-module-header justify-content-center">
                      <div
                        className="welcome-module-icon"
                        style={{
                          background:
                            "linear-gradient(135deg, #fd7e14, #e8590c)",
                        }}
                      >
                        <IconWaveSawTool size={24} />
                      </div>
                    </div>
                    <h6 className="welcome-module-title">
                      Conversão dos Agentes
                    </h6>
                    <p>
                      {metricsData?.conversionRate ? (
                        <>
                          <strong>{metricsData.conversionRate}%</strong> já
                          estão em uso
                        </>
                      ) : (
                        <span>Nenhum dado disponível</span>
                      )}
                    </p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4} className="mb-4">
                <Card className="welcome-module-card h-100">
                  <Card.Body className="text-center">
                    <div className="welcome-module-header justify-content-center">
                      <div
                        className="welcome-module-icon"
                        style={{
                          background:
                            "linear-gradient(135deg, #28a745, #20c997)",
                        }}
                      >
                        <IconUsers size={24} />
                      </div>
                    </div>
                    <h6 className="welcome-module-title">Usuários Ativos</h6>
                    <p>
                      {metricsData?.activeUsers ? (
                        <>
                          <strong>{metricsData.activeUsers}</strong> usuário
                          {metricsData.activeUsers > 1 ? "s" : ""} nas subcontas
                        </>
                      ) : (
                        <span>Nenhum dado disponível</span>
                      )}
                    </p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4} className="mb-4">
                <Card className="welcome-module-card h-100">
                  <Card.Body className="text-center">
                    <div className="welcome-module-header justify-content-center">
                      <div
                        className="welcome-module-icon"
                        style={{
                          background:
                            "linear-gradient(135deg, #de3096, #e83e8c)",
                        }}
                      >
                        <IconCheck size={24} />
                      </div>
                    </div>
                    <h6 className="welcome-module-title">Taxa de Churn</h6>
                    <p>
                      {metricsData?.churnRate !== undefined ? (
                        <>
                          <strong>{metricsData.churnRate}%</strong>{" "}
                          cancelamentos
                        </>
                      ) : (
                        <span>Nenhum dado disponível</span>
                      )}
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        </Container>
      </section>

      {/* Visão de Operações */}
      <section>
        <Container>
          <div className="welcome-category-section">
            <div className="welcome-category-header">
              <h2 className="welcome-category-title">
                <IconEye className="me-2" />
                Visão de Operações
              </h2>
            </div>
            <Row>
              <Col md={4} className="mb-4">
                <Card className="welcome-module-card h-100">
                  <Card.Body className="text-center">
                    <div className="welcome-module-header justify-content-center">
                      <div
                        className="welcome-module-icon"
                        style={{
                          background:
                            "var(--gradient-primary)",
                        }}
                      >
                        <IconTrendingUp size={24} />
                      </div>
                    </div>
                    <h6 className="welcome-module-title">Crescimento</h6>
                    <p>
                      {metricsData?.activeSubaccounts ? (
                        <>
                          <strong>{metricsData.activeSubaccounts}</strong>{" "}
                          clientes ativos
                        </>
                      ) : (
                        <span>Nenhum dado disponível</span>
                      )}
                    </p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4} className="mb-4">
                <Card className="welcome-module-card h-100">
                  <Card.Body className="text-center">
                    <div className="welcome-module-header justify-content-center">
                      <div
                        className="welcome-module-icon"
                        style={{
                          background:
                            "var(--gradient-primary)",
                        }}
                      >
                        <IconRobot size={24} />
                      </div>
                    </div>
                    <h6 className="welcome-module-title">Agentes Ativos</h6>
                    <p>
                      {metricsData?.implementedAgents ? (
                        <>
                          <strong>{metricsData.implementedAgents}</strong> em
                          uso
                        </>
                      ) : (
                        <span>Nenhum dado disponível</span>
                      )}
                    </p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4} className="mb-4">
                <Card className="welcome-module-card h-100">
                  <Card.Body className="text-center">
                    <div className="welcome-module-header justify-content-center">
                      <div
                        className="welcome-module-icon"
                        style={{
                          background:
                            "linear-gradient(135deg, #2bc155, #20c997)",
                        }}
                      >
                        <IconMessageCircle size={24} />
                      </div>
                    </div>
                    <h6 className="welcome-module-title">Conversas</h6>
                    <p>
                      {metricsData?.totalMessages ? (
                        <>
                          <strong>{metricsData.totalMessages}</strong> nesta
                          semana
                        </>
                      ) : (
                        <span>Nenhum dado disponível</span>
                      )}
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        </Container>
      </section>

      {/* Visão Rápida e Top Performers */}
      <section>
        <Container>
          <Row className="g-4">
            <Col md={6}>
              <Card className="welcome-tips-card">
                <Card.Body>
                  <div className="welcome-tips-header">
                    <IconEye size={20} />
                    <h5>Visão Rápida</h5>
                  </div>
                  <Row className="g-3 mt-3">
                    <Col md={4}>
                      <div className="text-center p-3 bg-light rounded">
                        <div>
                          <IconCreditCard size={24} />
                        </div>
                        <div>
                          <span>Nenhum dado disponível</span>
                        </div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="text-center p-3 bg-light rounded">
                        <div>
                          <IconSpeakerphone size={24} />
                        </div>
                        <div>
                          <span>Nenhum dado disponível</span>
                        </div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="text-center p-3 bg-light rounded">
                        <div>
                          <IconPalette size={24} />
                        </div>
                        <div>
                          <span>Nenhum dado disponível</span>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            {/* Clientes em Destaque */}
            <Col md={6}>
              <Card className="welcome-tips-card">
                <Card.Body>
                  <div className="welcome-tips-header">
                    <IconTrophy size={20} />
                    <h5>Top Performers do mês</h5>
                  </div>
                  <div className="mt-3">
                    {topPerformers.length > 0 ? (
                      topPerformers.map((performer, index) => (
                        <div
                          key={index}
                          className="d-flex align-items-center mb-3"
                        >
                          <span className="me-2">{performer.emoji}</span>
                          <img
                            src="https://via.placeholder.com/50"
                            alt="Client"
                            className="rounded me-3"
                            style={{ width: "50px", height: "50px" }}
                          />
                          <div>
                            <strong>{performer.name}</strong>
                            <br />
                            <small>{performer.messages} conversas</small>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-muted">Nenhum dado disponível</p>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Footer com CTA */}
      {[2, 5, 9, 10, 11].includes(getUserPlan()) && (
        <section className="welcome-footer">
          <Container>
            <Card className="welcome-tips-card">
              <Card.Body>
                <div className="text-center">
                  <div className="welcome-tips-header justify-content-center">
                    <IconHandStop size={20} />
                    <h4>Seus Clientes Precisam de Você</h4>
                  </div>
                  <p>
                    Responda dúvidas, acompanhe tickets e mantenha sua base
                    engajada.
                  </p>
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() => (window.location.href = "/support")}
                  >
                    Abrir Central de Suporte
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Container>
        </section>
      )}
    </div>
  );
};

export default AgencyWelcome;